import { readImageFileAsDataUrl } from "@/character/frame";
import {
  createGmPartTemplate,
  defaultNewPartForm,
  deleteGmPartFromCatalog,
  GM_SCENE_PART_KINDS,
  gmPartTemplateToPart,
  gmSceneKindLabel,
  hydrateGmPartCatalog,
  isGmScenePartKind,
  isStorageQuotaError,
  loadGmPartCatalog,
  type GmPartTemplate,
  upsertGmPartCatalog,
} from "@/gm/gm-part-catalog";
import { mountKindPicker, type KindPickerHandle } from "@/home/kind-picker";
import { mountPartUploadFlow, type PartUploadFlowHandle } from "@/home/mount-part-upload-flow";
import type { HomePartDraft } from "@/home/mount-sheet-crop";
import { kindPreset, normalizePartKind, type HomePartKind } from "@/home/part-kind";
import { loadHomeRoom, saveHomeRoom } from "@/home/storage";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

type FormState = Omit<GmPartTemplate, "version" | "createdAt" | "updatedAt"> & {
  createdAt?: number;
};

type PartsTab = "all" | HomePartKind;

let draftImageUrl: string | null = null;
let editingId: string | null = null;
let activeTab: PartsTab = "all";

function formFromDefaults(): FormState {
  draftImageUrl = null;
  editingId = null;
  return { ...defaultNewPartForm(), id: "" };
}

let formState: FormState = formFromDefaults();

function previewImageUrl(): string {
  return draftImageUrl ?? formState.imageDataUrl;
}

function defaultKindForTab(): HomePartKind {
  if (activeTab !== "all" && isGmScenePartKind(activeTab)) return activeTab;
  return "solid";
}

function itemsForTab(tab: PartsTab): GmPartTemplate[] {
  const all = loadGmPartCatalog();
  if (tab === "all") return all;
  return all.filter((p) => p.kind === tab);
}

function renderPartCard(m: GmPartTemplate): string {
  return `
    <article class="gm-monster-card" role="listitem" data-part-id="${escapeHtml(m.id)}">
      <div class="gm-monster-card-art-wrap">
        <img class="gm-monster-card-art" src="${escapeHtml(m.imageDataUrl)}" alt="" width="72" height="72" loading="lazy" />
        <span class="gm-monster-card-badge gm-parts-kind-badge">${escapeHtml(gmSceneKindLabel(m.kind))}</span>
      </div>
      <div class="gm-monster-card-body">
        <h3 class="gm-monster-card-title">${escapeHtml(m.name)}</h3>
        <p class="gm-monster-card-stats hint">${escapeHtml(kindPreset(m.kind).labelTh)}</p>
        <p class="gm-monster-card-stats hint">${m.cellsW}×${m.cellsH} ช่อง</p>
      </div>
      <footer class="gm-monster-card-actions">
        <button type="button" class="btn btn-ghost btn-xs gm-part-edit" data-id="${escapeHtml(m.id)}">แก้ไข</button>
        <button type="button" class="btn btn-ghost btn-xs gm-part-home" data-id="${escapeHtml(m.id)}">คลังบ้าน</button>
        <button type="button" class="btn btn-ghost btn-xs gm-part-delete" data-id="${escapeHtml(m.id)}">ลบ</button>
      </footer>
    </article>`;
}

function renderCategoryBlock(kind: HomePartKind, items: GmPartTemplate[]): string {
  if (items.length === 0) return "";
  const preset = kindPreset(kind);
  return `
    <section class="gm-parts-category" aria-labelledby="gm-parts-cat-${kind}">
      <header class="gm-parts-category-head">
        <h3 class="gm-parts-category-title" id="gm-parts-cat-${kind}">${escapeHtml(preset.tabLabelTh)}</h3>
        <p class="hint gm-parts-category-hint">${escapeHtml(preset.hintTh)} · ${items.length} ชิ้น</p>
      </header>
      <div class="gm-monster-card-grid" role="list">
        ${items.map(renderPartCard).join("")}
      </div>
    </section>
  `;
}

function renderCatalogBody(): string {
  if (activeTab !== "all") {
    const items = itemsForTab(activeTab);
    if (items.length === 0) {
      return `
        <div class="gm-monster-empty">
          <p>ยังไม่มีชิ้นในหมวด «${escapeHtml(gmSceneKindLabel(activeTab))}»</p>
          <p class="hint">กด <strong>+</strong> เพื่ออัปโหลดชิ้นใหม่</p>
        </div>
      `;
    }
    return `<div class="gm-monster-card-grid" role="list">${items.map(renderPartCard).join("")}</div>`;
  }

  const all = loadGmPartCatalog();
  if (all.length === 0) {
    return `
      <div class="gm-monster-empty">
        <p>ยังไม่มีชิ้นส่วนฉาก</p>
        <p class="hint">อัปโหลดแบบเดียวกับหน้าบ้าน · แยกชิ้น · ตั้งหมวด · sync เมื่อเปิด <a href="/home">/home</a></p>
      </div>
    `;
  }

  return GM_SCENE_PART_KINDS.map((kind) =>
    renderCategoryBlock(
      kind,
      all.filter((p) => p.kind === kind),
    ),
  ).join("");
}

function renderTabs(): string {
  const tabs: { id: PartsTab; label: string }[] = [
    { id: "all", label: "ทั้งหมด" },
    ...GM_SCENE_PART_KINDS.map((kind) => ({
      id: kind as PartsTab,
      label: kindPreset(kind).tabLabelTh,
    })),
  ];
  return tabs
    .map((tab) => {
      const active = tab.id === activeTab ? " gm-parts-tab--active" : "";
      return `<button type="button" class="gm-parts-tab${active}" data-parts-tab="${tab.id}">${escapeHtml(tab.label)}</button>`;
    })
    .join("");
}

function renderEditForm(): string {
  const f = formState;
  const img = previewImageUrl();
  return `
    <form class="gm-monster-form" id="gm-part-edit-form">
      <section class="gm-monster-form-section" aria-labelledby="gm-part-sec-image">
        <h3 class="gm-monster-form-section-title" id="gm-part-sec-image">รูปชิ้น</h3>
        <p class="hint gm-monster-form-lead">แก้ไขรายการเดิม · อัปโหลดใหม่แทนรูปได้</p>
        <div class="gm-monster-upload-zone">
          <label class="gm-monster-upload-label">
            <span class="gm-monster-upload-icon" aria-hidden="true">↑</span>
            <span class="gm-monster-upload-text">เปลี่ยนรูป</span>
            <input type="file" id="gm-part-image-file" accept="image/png,image/jpeg,image/webp" hidden />
          </label>
          <div class="gm-monster-preview-slot">
            <img class="gm-part-form-preview" src="${escapeHtml(img)}" alt="" width="120" height="120" />
          </div>
        </div>
      </section>

      <section class="gm-monster-form-section" aria-labelledby="gm-part-sec-info">
        <h3 class="gm-monster-form-section-title" id="gm-part-sec-info">ข้อมูล</h3>
        <label class="field gm-monster-field">
          <span class="field-label">ชื่อ</span>
          <input type="text" class="input" id="gm-part-name" value="${escapeHtml(f.name)}" maxlength="80" required />
        </label>
        <div class="field home-part-kind-field">
          <span class="field-label">หมวด</span>
          <div id="gm-part-edit-kind-host"></div>
        </div>
        <div class="gm-monster-grid gm-monster-grid--footprint">
          <label class="field gm-monster-field"><span class="field-label">กว้าง (ช่อง)</span>
            <input type="number" class="input" id="gm-part-cells-w" min="1" max="4" value="${f.cellsW}" /></label>
          <label class="field gm-monster-field"><span class="field-label">สูง (ช่อง)</span>
            <input type="number" class="input" id="gm-part-cells-h" min="1" max="4" value="${f.cellsH}" /></label>
        </div>
      </section>

      <p class="hint gm-monster-form-status" id="gm-part-status" role="status"></p>
      <div class="gm-monster-modal-foot">
        <button type="button" class="btn btn-ghost" id="gm-part-cancel-edit">ยกเลิก</button>
        <button type="submit" class="btn btn-primary">บันทึก</button>
      </div>
    </form>
  `;
}

function modalTitle(): string {
  return editingId ? "แก้ไขชิ้นส่วน" : "อัปโหลดชิ้นส่วน";
}

export function renderGmPartsSectionHtml(): string {
  const count = loadGmPartCatalog().length;
  return `
    <p class="hint gm-settings-lead">
      คลังชิ้นส่วนฉาก GM · อัปโหลด/แยกชิ้นเหมือนหน้าบ้าน · sync คลังเมื่อเปิด <a href="/home">/home</a>
    </p>
    <div class="gm-monsters-page gm-parts-page">
      <header class="gm-monsters-toolbar">
        <div class="gm-monsters-toolbar-text">
          <h2 class="gm-monsters-toolbar-title">ชิ้นส่วนตามหมวด</h2>
          <p class="hint" id="gm-part-catalog-count">${count} รายการ</p>
        </div>
        <button type="button" class="gm-monster-fab" id="gm-part-open-create" aria-label="เพิ่มชิ้น">
          <span aria-hidden="true">+</span>
        </button>
      </header>
      <nav class="gm-parts-tabs" aria-label="หมวดชิ้น">${renderTabs()}</nav>
      <div id="gm-part-list-host">${renderCatalogBody()}</div>
      <p class="hint gm-monster-toast" id="gm-part-toast" role="status"></p>
    </div>

    <div id="gm-part-modal" class="gm-monster-modal" hidden>
      <button type="button" class="gm-monster-modal-backdrop" id="gm-part-modal-backdrop" aria-label="ปิด"></button>
      <div
        class="gm-monster-modal-dialog gm-part-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gm-part-modal-title"
      >
        <header class="gm-monster-modal-head">
          <h2 class="gm-monster-modal-title" id="gm-part-modal-title">${modalTitle()}</h2>
          <button type="button" class="btn btn-ghost btn-sm gm-monster-modal-close" id="gm-part-modal-close" aria-label="ปิด">✕</button>
        </header>
        <div class="gm-monster-modal-body gm-part-modal-body" id="gm-part-modal-body"></div>
      </div>
    </div>
  `;
}

function readEditForm(root: HTMLElement, kindPicker: KindPickerHandle | undefined): FormState | null {
  const name = root.querySelector<HTMLInputElement>("#gm-part-name")?.value.trim();
  if (!name) return null;
  const num = (id: string) => Math.floor(Number(root.querySelector<HTMLInputElement>(`#${id}`)?.value ?? 0));
  const kind = kindPicker?.getValue() ?? formState.kind;
  return {
    ...formState,
    id: editingId ?? "",
    name,
    kind: normalizePartKind(kind),
    cellsW: num("gm-part-cells-w"),
    cellsH: num("gm-part-cells-h"),
    imageDataUrl: previewImageUrl(),
  };
}

function refreshCatalog(root: HTMLElement): void {
  const host = root.querySelector<HTMLElement>("#gm-part-list-host");
  const countEl = root.querySelector<HTMLElement>("#gm-part-catalog-count");
  const tabs = root.querySelector<HTMLElement>(".gm-parts-tabs");
  if (host) host.innerHTML = renderCatalogBody();
  if (countEl) countEl.textContent = `${loadGmPartCatalog().length} รายการ`;
  if (tabs) tabs.innerHTML = renderTabs();
}

function setStatus(root: HTMLElement, msg: string): void {
  const el = root.querySelector<HTMLElement>("#gm-part-status");
  if (el) el.textContent = msg;
}

function setToast(root: HTMLElement, msg: string): void {
  const el = root.querySelector<HTMLElement>("#gm-part-toast");
  if (el) el.textContent = msg;
}

export async function prepareGmPartsSection(): Promise<void> {
  await hydrateGmPartCatalog();
}

export function bindGmPartsSection(root: HTMLElement, userId: string): () => void {
  let uploadFlow: PartUploadFlowHandle | undefined;
  let editKindPicker: KindPickerHandle | undefined;

  const destroyUploadFlow = () => {
    uploadFlow?.destroy();
    uploadFlow = undefined;
  };

  const closeModal = () => {
    destroyUploadFlow();
    editKindPicker = undefined;
    const modal = root.querySelector<HTMLElement>("#gm-part-modal");
    const dialog = root.querySelector<HTMLElement>(".gm-part-modal-dialog");
    if (modal) modal.hidden = true;
    if (dialog) dialog.classList.remove("gm-part-modal-dialog--sheet");
    document.body.classList.remove("gm-monster-modal-open");
  };

  const persistDrafts = async (drafts: HomePartDraft[]) => {
    let lastName = "";
    for (const draft of drafts) {
      const kind = normalizePartKind(draft.kind);
      const entry = createGmPartTemplate({
        name: draft.name,
        imageDataUrl: draft.imageDataUrl,
        cellsW: draft.cellsW,
        cellsH: draft.cellsH,
        kind: isGmScenePartKind(kind) ? kind : defaultKindForTab(),
      });
      await upsertGmPartCatalog(entry);
      lastName = entry.name;
    }
    closeModal();
    formState = formFromDefaults();
    editingId = null;
    refreshCatalog(root);
    setToast(
      root,
      drafts.length > 1 ? `เพิ่ม ${drafts.length} ชิ้นแล้ว` : `เพิ่ม «${lastName}» แล้ว`,
    );
  };

  const mountCreateFlow = () => {
    destroyUploadFlow();
    editKindPicker = undefined;
    const body = root.querySelector<HTMLElement>("#gm-part-modal-body");
    const dialog = root.querySelector<HTMLElement>(".gm-part-modal-dialog");
    const title = root.querySelector<HTMLElement>("#gm-part-modal-title");
    if (title) title.textContent = modalTitle();
    if (dialog) dialog.classList.remove("gm-part-modal-dialog--sheet");
    if (!body) return;
    body.innerHTML = `<div id="gm-part-upload-flow-host"></div>`;
    const host = body.querySelector<HTMLElement>("#gm-part-upload-flow-host");
    if (!host) return;
    uploadFlow = mountPartUploadFlow(host, {
      allowedKinds: GM_SCENE_PART_KINDS,
      defaultKind: defaultKindForTab(),
      createButtonLabel: "เพิ่มชิ้นที่เลือก",
      onCreate: (drafts) => persistDrafts(drafts),
    });
    host.addEventListener(
      "home-sheet-step",
      (event) => {
        const step = (event as CustomEvent<{ step?: string }>).detail?.step;
        if (dialog) dialog.classList.toggle("gm-part-modal-dialog--sheet", step === "review");
      },
      { capture: true },
    );
  };

  const mountEditForm = () => {
    destroyUploadFlow();
    const body = root.querySelector<HTMLElement>("#gm-part-modal-body");
    const dialog = root.querySelector<HTMLElement>(".gm-part-modal-dialog");
    const title = root.querySelector<HTMLElement>("#gm-part-modal-title");
    if (title) title.textContent = modalTitle();
    if (dialog) dialog.classList.remove("gm-part-modal-dialog--sheet");
    if (!body) return;
    body.innerHTML = renderEditForm();
    const kindHost = body.querySelector<HTMLElement>("#gm-part-edit-kind-host");
    if (kindHost) {
      editKindPicker = mountKindPicker(formState.kind, {
        id: "gm-part-edit-kind",
        kinds: GM_SCENE_PART_KINDS,
      });
      kindHost.replaceChildren(editKindPicker.root);
    }
  };

  const openModal = (id?: string) => {
    if (id) {
      const existing = loadGmPartCatalog().find((p) => p.id === id);
      if (!existing) return;
      editingId = id;
      draftImageUrl = null;
      formState = { ...existing };
    } else {
      formState = formFromDefaults();
      editingId = null;
    }
    const modal = root.querySelector<HTMLElement>("#gm-part-modal");
    if (modal) modal.hidden = false;
    document.body.classList.add("gm-monster-modal-open");
    if (editingId) mountEditForm();
    else mountCreateFlow();
  };

  const onSubmit = async (event: Event) => {
    if (!(event.target instanceof HTMLFormElement) || event.target.id !== "gm-part-edit-form") return;
    event.preventDefault();
    const data = readEditForm(root, editKindPicker);
    if (!data) {
      setStatus(root, "กรอกชื่อชิ้น");
      return;
    }
    try {
      const entry = createGmPartTemplate({
        ...data,
        id: editingId ?? undefined,
        createdAt: formState.createdAt,
      });
      await upsertGmPartCatalog(entry);
      closeModal();
      formState = formFromDefaults();
      editingId = null;
      refreshCatalog(root);
      setToast(root, `บันทึก «${entry.name}» แล้ว`);
    } catch (err) {
      setStatus(
        root,
        isStorageQuotaError(err)
          ? "พื้นที่เบราว์เซอร์เต็ม — ลบรูปเก่าหรือย่อขนาด"
          : "บันทึกไม่สำเร็จ",
      );
    }
  };

  const onClick = async (event: Event) => {
    const target = event.target as HTMLElement;

    const tabBtn = target.closest<HTMLElement>("[data-parts-tab]");
    if (tabBtn?.dataset.partsTab) {
      activeTab = tabBtn.dataset.partsTab as PartsTab;
      refreshCatalog(root);
      return;
    }

    if (target.id === "gm-part-open-create" || target.closest("#gm-part-open-create")) {
      openModal();
      return;
    }
    if (
      target.id === "gm-part-modal-close" ||
      target.closest("#gm-part-modal-close") ||
      target.id === "gm-part-modal-backdrop" ||
      target.closest("#gm-part-modal-backdrop")
    ) {
      closeModal();
      formState = formFromDefaults();
      editingId = null;
      return;
    }
    if (target.id === "gm-part-cancel-edit" || target.closest("#gm-part-cancel-edit")) {
      closeModal();
      formState = formFromDefaults();
      editingId = null;
      return;
    }

    const editBtn = target.closest<HTMLElement>(".gm-part-edit");
    if (editBtn?.dataset.id) {
      openModal(editBtn.dataset.id);
      return;
    }
    const delBtn = target.closest<HTMLElement>(".gm-part-delete");
    if (delBtn?.dataset.id) {
      if (!window.confirm("ลบชิ้นจากแคตตาล็อก GM?")) return;
      try {
        await deleteGmPartFromCatalog(delBtn.dataset.id);
      } catch (err) {
        setToast(
          root,
          isStorageQuotaError(err)
            ? "ลบไม่สำเร็จ — พื้นที่เบราว์เซอร์เต็ม"
            : "ลบไม่สำเร็จ",
        );
        return;
      }
      refreshCatalog(root);
      setToast(root, "ลบแล้ว");
      return;
    }
    const homeBtn = target.closest<HTMLElement>(".gm-part-home");
    if (homeBtn?.dataset.id) {
      const m = loadGmPartCatalog().find((x) => x.id === homeBtn.dataset.id);
      if (!m) return;
      const home = await loadHomeRoom(userId);
      const part = gmPartTemplateToPart(m);
      const idx = home.parts.findIndex((p) => p.id === part.id);
      if (idx >= 0) home.parts[idx] = part;
      else home.parts.push(part);
      await saveHomeRoom(userId, home);
      setToast(root, `ใส่ «${m.name}» ในคลังบ้านแล้ว`);
    }
  };

  const onChange = async (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.id !== "gm-part-image-file") return;
    const input = target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      draftImageUrl = await readImageFileAsDataUrl(file);
      const preview = root.querySelector<HTMLImageElement>(".gm-part-form-preview");
      if (preview) preview.src = draftImageUrl;
      setStatus(root, "เปลี่ยนรูปแล้ว — กดบันทึก");
    } catch (err) {
      setStatus(root, err instanceof Error ? err.message : "อัปโหลดไม่สำเร็จ");
      input.value = "";
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    const modal = root.querySelector<HTMLElement>("#gm-part-modal");
    if (!modal || modal.hidden) return;
    closeModal();
    formState = formFromDefaults();
    editingId = null;
  };

  root.addEventListener("submit", onSubmit);
  root.addEventListener("click", onClick);
  root.addEventListener("change", onChange);
  document.addEventListener("keydown", onKeyDown);

  return () => {
    document.removeEventListener("keydown", onKeyDown);
    closeModal();
    root.removeEventListener("submit", onSubmit);
    root.removeEventListener("click", onClick);
    root.removeEventListener("change", onChange);
  };
}
